import React from 'react';
import ReactDOM from 'react-dom/client';
// Tipografías del manual (solo subset latin: cubre español). Self-hosted para
// que la PWA las tenga offline (el SW precachea los .woff2 del build).
import '@fontsource/inter/latin-400.css';
import '@fontsource/inter/latin-500.css';
import '@fontsource/inter/latin-600.css';
import '@fontsource/inter/latin-700.css'; // la app usa font-bold: sin este peso el navegador lo simula
import '@fontsource/onest/latin-600.css';
import '@fontsource/onest/latin-700.css';
import '@fontsource/onest/latin-800.css';
import './index.css';
import App from './App';
import { AuthProvider } from './contexts/AuthContext';
import { OfflineProvider } from './contexts/OfflineContext';
import { SyncProvider } from './contexts/SyncContext';
import { ModalProvider } from './contexts/ModalContext';
import { initClarity } from './libs/clarity';
import { initGtag } from './libs/gtag';

// --- Redirección canónica micampodigital.com -> micampodigital.com.ar ---
// DEFENSA EN PROFUNDIDAD (no es el fix principal; ese vive en vercel.json +
// api/sw-unregister.ts para descongelar el SW viejo en .com).
//
// Si por algún motivo el HTML/JS de la app llega a ejecutarse en un host .com
// (ej. el SW real se actualizó tarde, o un cache HTTP sirvió la shell), este
// check redirige por JS al dominio canónico .com.ar preservando la ruta.
// Usa replace() para no ensuciar el historial (sin entrada de "atrás").
// NO se ejecuta en .com.ar (dominio canónico) ni en otros hosts (ngrok, preview).
const CANONICAL_HOST = 'micampodigital.com.ar';
const DEPRECATED_HOSTS = ['micampodigital.com', 'www.micampodigital.com'];
if (typeof window !== 'undefined' && DEPRECATED_HOSTS.includes(window.location.hostname)) {
  window.location.replace(
    `https://${CANONICAL_HOST}${window.location.pathname}${window.location.search}${window.location.hash}`
  );
}

// Microsoft Clarity (grabaciones + heatmaps) y Google Analytics 4 (pageviews).
// Temprano en el arranque para capturar desde la primera pantalla; los guards
// compartidos viven en analyticsGuards.ts.
initClarity();
initGtag();

// --- Handler global de ChunkLoadError ---
// Tras cada deploy, los nombres de los chunks cambian (hash de contenido).
// Si un cliente tiene un index.html cacheado (por el Service Worker o por el
// caché HTTP del navegador) que referencia chunks viejos, la carga lazy falla:
// el servidor no encuentra el .js, devuelve el index.html del fallback del SPA
// (empieza con '<') y se produce un "Unexpected token '<'" / ChunkLoadError
//
// Solución: recargar la página una sola vez para forzar la descarga del
// index.html nuevo (con los hashes de chunk correctos). Se limita con un
// contador en sessionStorage para no entrar en bucle de recargas.
const CHUNK_RELOAD_KEY = '__chunk_reload_count';
function handleChunkLoadFailure(): void {
  if (typeof window === 'undefined' || typeof sessionStorage === 'undefined') return;
  const count = Number(sessionStorage.getItem(CHUNK_RELOAD_KEY) || '0');
  if (count >= 1) return; // Ya intentamos recargar una vez, no seguir (evita bucle)
  sessionStorage.setItem(CHUNK_RELOAD_KEY, String(count + 1));
  // Recarga dura: ignora caché para traer el index.html más reciente.
  window.location.reload();
}

if (typeof window !== 'undefined') {
  // 1) Errores no capturados de los dynamic imports (lazy) -> ChunkLoadError.
  window.addEventListener('unhandledrejection', (event) => {
    const reason = event?.reason;
    const name = reason?.name || '';
    const message = reason?.message || '';
    if (
      name === 'ChunkLoadError' ||
      name === 'SyntaxError' ||
      /Loading chunk .* failed|Failed to fetch dynamically imported module/i.test(message)
    ) {
      handleChunkLoadFailure();
    }
  });

  // 2) Errores de carga de etiquetas <script> (jsonp chunk loading).
  window.addEventListener('error', (event) => {
    const target = event.target as any;
    if (target && target.tagName === 'SCRIPT' && typeof target.src === 'string' && /\/static\/js\//.test(target.src)) {
      handleChunkLoadFailure();
    }
  }, true);
}

// Prevenir flechas arriba/abajo en inputs numéricos
// Esto evita cambios accidentales al presionar flechas o hacer scroll
document.addEventListener('keydown', (e) => {
  // Solo prevenir en inputs numéricos
  if (e.target instanceof HTMLInputElement && e.target.type === 'number') {
    if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
      e.preventDefault();
    }
  }
});

// Prevenir cambios con rueda del mouse en inputs numéricos
document.addEventListener('wheel', (e) => {
  if (e.target instanceof HTMLInputElement && e.target.type === 'number') {
    (e.target as HTMLInputElement).blur();
  }
}, { passive: true });

// Calcula la URL ABSOLUTA del service-worker.js a partir de dónde se carga
// realmente el bundle principal, en vez de usar un path relativo a la ruta
// actual. Con homepage relativa y BrowserRouter, registrar 'service-worker.js'
// se resolvía contra la URL del navegador (ej. /animals/service-worker.js),
// que no existe -> el server devolvía index.html (MIME text/html) y fallaba el
// registro con "unsupported MIME type". Derivar desde el script garantiza la
// base correcta sea cual sea la ruta ni el subpath de deploy (raíz o /sesiones/).
function getServiceWorkerUrl(): string {
  try {
    const mainScript = Array.from(document.getElementsByTagName('script'))
      .map((s) => s.src)
      .find((src) => /\/static\/js\/main\.[a-f0-9]+\.chunk\.js$/.test(src));
    if (mainScript) {
      // mainScript = <base>/static/js/main.HASH.chunk.js -> base = todo antes de /static/js
      const base = mainScript.replace(/\/static\/js\/.*$/, '');
      return `${base}/service-worker.js`;
    }
  } catch {
    /* fallthrough */
  }
  // Fallback: raíz del origen.
  return '/service-worker.js';
}

// Register service worker
if ('serviceWorker' in navigator) {
  console.log('INDEX: Intentando registrar Service Worker...');
  window.addEventListener('load', () => {
    // Pedir storage persistente al boot: sin esto, Chrome puede evictar TODO el
    // storage del origen (Cache Storage del SW + IndexedDB/Dexie) bajo presión
    // de espacio, y la app deja de funcionar offline silenciosamente (caso real
    // en Chrome Android). El patrón ya se usaba al grabar notas de voz
    // (voiceNoteStore); acá lo pedimos siempre, en el primer load.
    try {
      const requestPersist = async () => {
        const already = navigator.storage?.persisted ? await navigator.storage.persisted() : false;
        if (!already && navigator.storage?.persist) {
          const granted = await navigator.storage.persist();
          console.log('INDEX: navigator.storage.persist() =', granted);
        }
      };
      void requestPersist();
    } catch {
      /* no soportado: seguir igual */
    }

    navigator.serviceWorker.register(getServiceWorkerUrl())
      .then((registration) => {
        console.log('INDEX: Service Worker registered successfully:', registration.scope);
        console.log('INDEX: SW state:', registration.active?.state);

        // Forzar el check de actualización del SW en cada carga, en vez de
        // esperar al ciclo del navegador (que por spec puede demorar hasta 24h).
        // Así, tras un deploy, el usuario obtiene el index.html nuevo en su
        // próxima visita casi de inmediato.
        registration.update().catch((err) => {
          console.warn('INDEX: SW update check failed:', err);
        });

        // Escuchar cambios en el estado del SW
        // AUTO-UPDATE: cuando llega un SW nuevo (nueva versión deployada), lo
        // activamos y recargamos automáticamente, SIN pedir confirmación al
        // usuario. Es imprescindible para esta PWA porque el SW precachea el
        // index.html: si dejáramos activo el SW viejo, seguiría sirviendo un
        // index.html que referencia chunks con hash viejo (que ya no existen
        // en el servidor) y se rompería con ChunkLoadError al cargar rutas lazy.
        //
        // RACE-SAFE: esperamos a que el nuevo SW realmente tome el control
        // (controllerchange) ANTES de recargar. Antes recargábamos apenas
        // mandábamos SKIP_WAITING, lo cual podía racear con la activación y
        // servir el index.html del SW viejo en esa recarga (motivo por el que
        // a veces hacía falta un force-refresh para ver la versión nueva).
        function activateNewWorkerAndReload(worker: ServiceWorker): void {
          navigator.serviceWorker.addEventListener(
            'controllerchange',
            () => {
              try { sessionStorage.removeItem('__chunk_reload_count'); } catch { /* noop */ }
              window.location.reload();
            },
            { once: true }
          );
          worker.postMessage({ type: 'SKIP_WAITING' });
        }

        registration.addEventListener('updatefound', () => {
          const newWorker = registration.installing;
          if (!newWorker) return;
          console.log('INDEX: Nuevo Service Worker encontrado');
          newWorker.addEventListener('statechange', () => {
            if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
              // Hay un SW nuevo esperando -> activarlo y recargar (race-safe).
              activateNewWorkerAndReload(newWorker);
            }
          });
        });

        // GAP CRÍTICO: si al cargar la página ya existe un SW en estado
        // 'waiting' (instalado en una sesión previa pero nunca activado, ej. el
        // usuario cerró la tab antes del reload), el evento 'updatefound' NO se
        // dispara y el usuario queda congelado en la versión vieja hasta un
        // force-refresh manual. Lo activamos y recargamos acá mismo.
        if (registration.waiting && navigator.serviceWorker.controller) {
          console.log('INDEX: SW en espera detectado al cargar, activando...');
          activateNewWorkerAndReload(registration.waiting);
        }
      })
      .catch((error) => {
        console.error('INDEX: Service Worker registration failed:', error);
      });
  });

  // MEJORA: Detectar reconexión y avisar al SW
  window.addEventListener('online', () => {
    console.log('INDEX: Conexión restablecida, notificando al SW...');
    if (navigator.serviceWorker.controller) {
      navigator.serviceWorker.controller.postMessage({
        type: 'SYNC_NOW'
      });
    }
  });
}

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);

// StrictMode doble-invoca los efectos en DEV, lo que re-inicializa el
// SyncManager y genera una race donde el evento de sync del repositorio a veces
// no se captura (un create queda sólo en IndexedDB). En producción StrictMode
// no doble-invoca, así que el sync es confiable. Para que los E2E reflejen el
// runtime de producción, desactivamos StrictMode sólo cuando corre bajo E2E
// (REACT_APP_E2E). Así los tests prueban el flujo real (evento → saveRemote).
const appTree = (
  <AuthProvider>
    <OfflineProvider>
      <SyncProvider>
        <ModalProvider>
          <App />
        </ModalProvider>
      </SyncProvider>
    </OfflineProvider>
  </AuthProvider>
);

root.render(
  process.env.REACT_APP_E2E ? appTree : <React.StrictMode>{appTree}</React.StrictMode>
);
